import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  LiveKitRoom,
  useTracks,
  AudioTrack,
  useRoomContext,
  useParticipants,
  isTrackReference,
  BarVisualizer,
  useVoiceAssistant,
  TrackToggle,
  MediaDeviceMenu,
  DisconnectButton,
  useSpeakingParticipants,
} from '@livekit/components-react';
import { Room, RoomConnectOptions, RoomOptions, Track, LocalAudioTrack } from 'livekit-client';
import { X } from 'lucide-react';

interface ConnectionDetails {
  serverUrl: string;
  participantToken: string;
}

interface UserChoices {
  audioEnabled: boolean;
  audioDeviceId?: string;
  userName: string;
}

interface AgentConversationPopoverProps {
  connectionDetails: ConnectionDetails | null;
  userChoices: UserChoices;
  onClose: () => void;
  meetingTitle: string;
}

const VoiceAssistantVisualizer = () => {
  const { state, audioTrack } = useVoiceAssistant();

  return (
    <div className="flex items-end justify-center h-40 mb-12">
      <BarVisualizer
        state={state}
        trackRef={audioTrack}
        barCount={5}
        options={{
          minHeight: 12,
          maxHeight: 100,
        }}
      />
    </div>
  );
};

const CustomControls = () => {
  const room = useRoomContext();
  const audioTracks = useTracks([{ source: Track.Source.Microphone, withPlaceholder: true }]);
  const localAudioTrack = useMemo(
    () =>
      audioTracks.find(tr => tr.participant.isLocal)?.publication?.track as
        | LocalAudioTrack
        | undefined,
    [audioTracks]
  );

  return (
    <div className="flex items-center space-x-4">
      <div className="lk-button-group">
        <TrackToggle source={Track.Source.Microphone} />
        <div className="lk-button-group-menu">
          <MediaDeviceMenu kind="audioinput" tracks={{ audioinput: localAudioTrack }} />
        </div>
      </div>
      <DisconnectButton onClick={() => room.disconnect()} className="lk-button-danger">
        <X size={20} />
      </DisconnectButton>
    </div>
  );
};

const InnerConversationRoom = ({
  onClose,
  meetingTitle,
}: {
  onClose: () => void;
  meetingTitle: string;
}) => {
  const room = useRoomContext();
  const audioTracks = useTracks([{ source: Track.Source.Microphone, withPlaceholder: true }]);
  const allParticipants = useParticipants();
  const agentParticipant = allParticipants.find(p => p.identity.startsWith('agent-'));
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (agentParticipant) {
      setIsConnected(true);
    }
  }, [agentParticipant]);

  return (
    <div className="fixed inset-0 bg-black backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="w-full h-full flex flex-col items-center justify-center relative">
        <button
          onClick={onClose}
          className="absolute top-8 right-8 text-white/80 hover:text-white transition-all duration-200 z-10 hover:scale-110"
        >
          <X size={28} />
        </button>

        <div className="text-white text-3xl font-light mb-8 text-center">
          {isConnected ? meetingTitle : 'Connecting...'}
        </div>

        <VoiceAssistantVisualizer />

        <div className="absolute bottom-12">
          <CustomControls />
        </div>
      </div>

      {audioTracks.filter(isTrackReference).map(trackRef => (
        <AudioTrack key={trackRef.publication.trackSid} trackRef={trackRef} />
      ))}
    </div>
  );
};

export const AgentConversationPopover: React.FC<AgentConversationPopoverProps> = ({
  connectionDetails,
  userChoices,
  onClose,
  meetingTitle,
}) => {
  const roomOptions = useMemo((): RoomOptions => {
    return {
      audioCaptureDefaults: {
        deviceId: userChoices.audioDeviceId ?? undefined,
      },
      publishDefaults: {
        dtx: false,
        red: true,
      },
      adaptiveStream: { pixelDensity: 'screen' },
      dynacast: true,
    };
  }, [userChoices]);

  const room = useMemo(() => new Room(roomOptions), [roomOptions]);

  const connectOptions = useMemo((): RoomConnectOptions => {
    return {
      autoSubscribe: true,
    };
  }, []);

  const handleOnLeave = useCallback(async () => {
    if (room) {
      await room.disconnect();
    }
    onClose();
  }, [room, onClose]);

  const handleError = useCallback(
    (error: Error) => {
      console.error('LiveKit error:', error);
      alert(`Encountered an unexpected error: ${error.message}`);
      onClose();
    },
    [onClose]
  );

  if (!connectionDetails) {
    return null;
  }

  return (
    <LiveKitRoom
      connect={true}
      room={room}
      token={connectionDetails.participantToken}
      serverUrl={connectionDetails.serverUrl}
      connectOptions={connectOptions}
      video={false}
      audio={userChoices.audioEnabled}
      onDisconnected={handleOnLeave}
      onError={handleError}
    >
      <InnerConversationRoom onClose={onClose} meetingTitle={meetingTitle} />
    </LiveKitRoom>
  );
};
